import { jwtExpiraEn } from "@/helpers/jwt";

class SessionService {
  constructor() {
    this.timeoutId = null;
    this.sessionDuration = 30 * 60 * 1000; // 30 min de inactividad
    // El token caduca a los 15 min: se renueva con el refresh token este
    // margen antes de su `exp`, sin esperar a que una petición dé 402.
    this.margenRenovacionMs = 60 * 1000;
    // Máximo de sesión desde el login: ni la actividad ni RenovarToken lo
    // alargan; sólo un logout + login nuevo lo reinicia.
    this.maxSesionMs = 30 * 24 * 60 * 60 * 1000;
    // `loginTimestamp` (persistido y compartido entre pestañas) se escribe
    // como mucho cada tanto, no en cada mousemove.
    this.persistenciaActividadMs = 10 * 1000;
    // Margen que da el aviso de inactividad (SessionExpiryAlert) para
    // renovar o cerrar. El intervalo de este servicio sólo cierra la sesión
    // pasado este margen: si usara el mismo umbral que el aviso, su tick de
    // 10 s podía adelantarse al de 1 s del aviso y mandar al login sin modal.
    this.inactivityGraceMs = 30 * 1000;
    this.lastActivity = Date.now();
    this.shouldTrackActivity = false;
    this.isHandlingExpiry = false;
    // Mientras la alerta de expiración esté visible, `resetTimer` deja de
    // refrescar `lastActivity` — si no, el simple hecho de mover el ratón
    // sobre el modal contaría como actividad y cerraría el aviso antes de
    // que el usuario pueda decidir si renovar o cerrar sesión.
    this.warningActive = false;
    // Callback que registra SessionExpiryAlert para avisar de que el servidor
    // ha rechazado el token (402/401/403) antes de mandar al login. Devuelve
    // una promesa que se resuelve cuando el usuario acepta o vence la cuenta
    // atrás. Mismo patrón que navigationGuard.
    this.tokenExpiredNotifier = null;
    this.expiryNoticeActive = false;
  }

  setTokenExpiredNotifier(notifier) {
    this.tokenExpiredNotifier = notifier;
  }

  async _getAuthStore() {
    const { useAuthStore } = await import("@/stores/auth");
    return useAuthStore();
  }

  startSessionTimer() {
    this.clearSessionTimer();
    this.lastActivity = Date.now();
    this.shouldTrackActivity = true;
    this.isHandlingExpiry = false;
    this.warningActive = false;

    this.timeoutId = setInterval(async () => {
      const auth = await this._getAuthStore();
      if (this.sesionMaximaAlcanzada(auth)) {
        await this.endSession("max_session");
        return;
      }
      if (this.debeRenovarToken(auth)) {
        auth.RenovarToken().catch(() => this.handleTokenExpiry());
      }
      // Mientras la alerta de expiración está visible es ella quien
      // gestiona el grace period (renovar / cerrar / countdown a logout).
      // Si dejamos este poll forzar el logout, cerraría la sesión antes
      // de que el usuario pueda reaccionar al modal.
      if (this.warningActive) return;
      // Red de seguridad por si el aviso no llegó a mostrarse (p. ej. no
      // montado): actúa sólo una vez agotado también su margen.
      const now = Date.now();
      const timeSinceLastActivity = now - this.ultimaActividad(auth);
      if (timeSinceLastActivity >= this.sessionDuration + this.inactivityGraceMs) {
        await this.endSession("timeout");
      }
    }, 10000);
  }

  clearSessionTimer() {
    if (this.timeoutId) {
      clearInterval(this.timeoutId);
      this.timeoutId = null;
    }
    this.shouldTrackActivity = false;
    this.warningActive = false;
  }

  async endSession(reason = "timeout") {
    if (this.isHandlingExpiry) return;
    this.isHandlingExpiry = true;

    try {
      this.clearSessionTimer();
      localStorage.removeItem("savedCredentials");

      const auth = await this._getAuthStore();
      // `isAuthManager` es un ref booleano, no una función — accederlo
      // como `auth.isAuthManager()` lanzaba TypeError silencioso y dejaba
      // sin limpiar el token de IndoManager al expirar la sesión.
      if (auth.isAuthManager) {
        await auth.ManagerLogout();
        localStorage.removeItem("brandUser");
      }
      await auth.Logout();

      const { default: router } = await import("@/router");
      if (router.currentRoute.value.path !== "/login") {
        await router.push("/login");
      }
    } catch (error) {
      const { default: router } = await import("@/router");
      if (router.currentRoute.value.path !== "/login") {
        await router.push("/login");
      }
    } finally {
      this.isHandlingExpiry = false;
    }
  }

  // Sin `sesionIniciadaEn` (sesiones previas al contador) cuenta como
  // agotada: obliga a un login nuevo, que lo inicia.
  sesionMaximaAlcanzada(auth) {
    if (auth.sesionIniciadaEn == null) return true;
    return Date.now() - auth.sesionIniciadaEn >= this.maxSesionMs;
  }

  debeRenovarToken(auth) {
    if (!auth.token || !auth.refreshToken) return false;
    const expira = jwtExpiraEn(auth.token);
    return expira != null && expira - Date.now() <= this.margenRenovacionMs;
  }

  // La actividad en cualquier pestaña cuenta: `loginTimestamp` se comparte
  // vía localStorage. Si no, una pestaña quieta cerraría la sesión de todas.
  ultimaActividad(auth) {
    return Math.max(this.lastActivity, auth.loginTimestamp ?? 0);
  }

  async resetTimer() {
    if (this.warningActive) return;
    const auth = await this._getAuthStore();
    if (auth.token && this.shouldTrackActivity) {
      const now = Date.now();
      this.lastActivity = now;
      if (now - (auth.loginTimestamp ?? 0) >= this.persistenciaActividadMs) {
        auth.loginTimestamp = now;
      }
    }
  }

  async isSessionExpired() {
    const auth = await this._getAuthStore();
    // Fuente de verdad: Pinia persist (token + loginTimestamp), no savedCredentials
    // (savedCredentials es solo autofill "recordarme" y su timestamp puede quedar obsoleto)
    if (!auth.token) return true;
    // Sesiones de antes del refresh token: no se pueden renovar.
    if (!auth.refreshToken) return true;
    if (this.sesionMaximaAlcanzada(auth)) return true;

    return Date.now() - this.ultimaActividad(auth) >= this.sessionDuration;
  }

  // Varias peticiones pueden devolver 402 a la vez: sólo la primera muestra
  // el aviso, el resto se ignoran mientras está abierto.
  async handleTokenExpiry() {
    if (this.expiryNoticeActive) return;
    this.expiryNoticeActive = true;
    try {
      if (this.tokenExpiredNotifier) await this.tokenExpiredNotifier();
    } finally {
      this.expiryNoticeActive = false;
    }
    await this.endSession("token_expired");
  }

  async isTokenValid() {
    const auth = await this._getAuthStore();
    const t = auth.token || auth.tokenManager;
    const authenticated = !!auth.token;
    return !!(t && authenticated && !(await this.isSessionExpired()));
  }
}

export default new SessionService();
